<template>
  <section class="exhibition-schedule" aria-labelledby="exhibition-schedule-title">
    <div class="exhibition-schedule__content">
      <header class="exhibition-schedule__heading">
        <h2 id="exhibition-schedule-title">{{ t('home.exhibitionTitle') }}</h2>
        <p>{{ t('home.exhibitionLead') }}</p>
      </header>

      <div ref="exhibitionSwiper" class="exhibition-swiper">
        <div class="swiper-wrapper exhibition-swiper__wrapper">
          <article
            v-for="exhibition in exhibitions"
            :key="exhibition.id"
            class="swiper-slide exhibition-card"
            :class="{
              'is-clickable': getTargetUrl(exhibition),
              'is-ended': exhibition.status === 'ENDED',
              'has-status-strip': exhibition.status === 'ENDED' || exhibition.status === 'ONGOING'
            }"
            :role="getTargetUrl(exhibition) ? 'link' : undefined"
            :tabindex="getTargetUrl(exhibition) ? 0 : -1"
            @click="handleExhibitionClick(exhibition, $event)"
            @keydown.enter.prevent="handleExhibitionClick(exhibition, $event)"
            @keydown.space.prevent="handleExhibitionClick(exhibition, $event)"
          >
            <img class="exhibition-card__image exhibition-media" :src="exhibition.coverUrl" :alt="`${exhibition.name} ${t('exhibition.venueSuffix')}`" width="330" height="330" loading="lazy" decoding="async" draggable="false" @load="markMediaReady" @error="markMediaReady">
            <span class="exhibition-card__overlay" />
            <div class="exhibition-card__copy">
              <h3>{{ exhibition.name }}</h3>
              <p>{{ exhibition.location }}</p>
              <strong>{{ formatDate(exhibition) }}</strong>
            </div>
            <div v-if="exhibition.status === 'ENDED' || exhibition.status === 'ONGOING'" class="exhibition-card__status" :class="`is-${String(exhibition.status || '').toLowerCase()}`">
              <img v-if="exhibition.status === 'ONGOING'" class="exhibition-card__status-texture" src="/static/images/home/exhibitions/exhibition-ongoing-texture.svg" alt="" aria-hidden="true">
              <span>{{ statusLabel(exhibition.status) }}</span>
            </div>
          </article>
        </div>
      </div>

      <div class="exhibition-schedule__controls" :aria-label="t('exhibition.carouselControls')">
        <button ref="previousButton" class="legacy-carousel-button exhibition-swiper__prev" type="button" :aria-label="t('exhibition.previousSlide')" :disabled="!canMovePrev" @click="showPreviousExhibitions">
          <span aria-hidden="true">←</span>
        </button>
        <button ref="nextButton" class="legacy-carousel-button exhibition-swiper__next" type="button" :aria-label="t('exhibition.nextSlide')" :disabled="!canMoveNext" @click="showNextExhibitions">
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  </section>

</template>

<script>
import { exhibitionAPI } from '@/utils/api'

const {
  formatExhibitionDate,
  mapPublicExhibitions,
  buildExhibitionSchedule,
  getExhibitionTargetUrl
} = require('@/data/exhibition-data.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { getExhibitionNavigationState } = require('@/utils/home-carousel.cjs')

export default {
  name: 'ExhibitionSchedule',
  mixins: [siteLanguageMixin],
  data() {
    return {
      exhibitionSwiper: null,
      activeIndex: 0,
      viewportResizeTimer: null,
      wheelNavigationTimer: null,
      wheelNavigationBound: false,
      wheelNavigationLocked: false,
      canMovePrev: false,
      canMoveNext: false,
      exhibitionRequestGuard: createLatestSiteRequestGuard(),
      exhibitions: []
    }
  },
  watch: {
    currentLanguage() {
      this.loadExhibitions()
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleViewportResize)
    this.syncExhibitionWheelListener()
    this.loadExhibitions()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleViewportResize)
    this.removeExhibitionWheelListener()
    window.clearTimeout(this.viewportResizeTimer)
    window.clearTimeout(this.wheelNavigationTimer)
    if (this.exhibitionSwiper) this.exhibitionSwiper.destroy(true, true)
  },
  methods: {
    formatDate(exhibition) {
      return formatExhibitionDate(exhibition, this.currentLanguage)
    },
    getTargetUrl: getExhibitionTargetUrl,
    statusLabel(status) {
      return ({
        ENDED: this.t('exhibition.ended'),
        ONGOING: this.t('exhibition.ongoing'),
        UPCOMING: this.t('exhibition.upcoming')
      })[status] || ''
    },
    markMediaReady(event) {
      const media = event && (event.currentTarget || event.target)
      if (media && media.classList) media.classList.add('is-media-ready')
    },
    async loadExhibitions() {
      const request = this.exhibitionRequestGuard.start(this.currentLanguage)
      try {
        const response = await exhibitionAPI.getSchedule(request.siteType)
        if (!this.exhibitionRequestGuard.isCurrent(request, this.currentLanguage)) return
        const schedule = buildExhibitionSchedule(mapPublicExhibitions(response && response.data))
        this.exhibitions = schedule.items
        this.activeIndex = schedule.initialSlide
      } catch (error) {
        if (!this.exhibitionRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.exhibitions = []
        console.error('Failed to load exhibition schedule:', error)
      } finally {
        if (this.exhibitionRequestGuard.isCurrent(request, this.currentLanguage)) {
          if (this.exhibitionSwiper) this.exhibitionSwiper.destroy(true, true)
          this.exhibitionSwiper = null
          this.$nextTick(() => {
            this.initSwiper()
            if (this.exhibitionSwiper) this.exhibitionSwiper.update()
          })
        }
      }
    },
    handleViewportResize() {
      this.syncExhibitionWheelListener()
      window.clearTimeout(this.viewportResizeTimer)
      this.viewportResizeTimer = window.setTimeout(() => {
        const swiper = this.exhibitionSwiper
        if (!swiper) return
        swiper.update()
        window.requestAnimationFrame(() => {
          this.updateNavigationState()
        })
      }, 120)
    },
    syncExhibitionWheelListener() {
      const container = this.$refs.exhibitionSwiper
      const shouldBindWheel = window.matchMedia('(min-width: 701px)').matches
      if (shouldBindWheel && container && !this.wheelNavigationBound) {
        container.addEventListener('wheel', this.handleExhibitionWheel, { passive: false })
        this.wheelNavigationBound = true
      } else if (!shouldBindWheel) {
        this.removeExhibitionWheelListener()
      }
    },
    removeExhibitionWheelListener() {
      const container = this.$refs.exhibitionSwiper
      if (container && this.wheelNavigationBound) {
        container.removeEventListener('wheel', this.handleExhibitionWheel)
      }
      this.wheelNavigationBound = false
      this.wheelNavigationLocked = false
      window.clearTimeout(this.wheelNavigationTimer)
    },
    handleExhibitionWheel(event) {
      if (!window.matchMedia('(min-width: 701px)').matches || !this.exhibitionSwiper) return
      this.updateNavigationState()
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      if (Math.abs(delta) < 24) return
      const isNext = delta > 0
      const canMove = isNext ? this.canMoveNext : this.canMovePrev
      if (this.wheelNavigationLocked) {
        if (canMove) event.preventDefault()
        return
      }
      if (!canMove) return

      event.preventDefault()
      this.wheelNavigationLocked = true
      isNext ? this.showNextExhibitions() : this.showPreviousExhibitions()
      window.clearTimeout(this.wheelNavigationTimer)
      this.wheelNavigationTimer = window.setTimeout(() => {
        this.wheelNavigationLocked = false
      }, 600)
    },
    initSwiper() {
      if (this.exhibitionSwiper || !window.Swiper) return
      const container = this.$refs.exhibitionSwiper
      if (!container) return
      this.exhibitionSwiper = new window.Swiper(container, {
        initialSlide: this.activeIndex,
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: 20,
        speed: 600,
        allowTouchMove: true,
        grabCursor: true,
        watchOverflow: true,
        navigation: {
          prevEl: this.$refs.previousButton,
          nextEl: this.$refs.nextButton,
          disabledClass: 'is-disabled',
          lockClass: 'is-locked'
        },
        a11y: {
          prevSlideMessage: this.t('exhibition.previousSlide'),
          nextSlideMessage: this.t('exhibition.nextSlide')
        },
        observer: true,
        observeParents: true
      })
      this.exhibitionSwiper.on('slideChange', () => this.updateSwiperState())
      this.exhibitionSwiper.on('reachBeginning', () => this.updateNavigationState())
      this.exhibitionSwiper.on('reachEnd', () => this.updateNavigationState())
      this.exhibitionSwiper.on('fromEdge', () => this.updateNavigationState())
      this.exhibitionSwiper.on('resize', () => this.updateNavigationState())
      this.exhibitionSwiper.on('observerUpdate', () => {
        window.requestAnimationFrame(() => this.updateNavigationState())
      })
      this.exhibitionSwiper.on('transitionEnd', () => this.updateNavigationState())
      this.updateSwiperState()
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          window.setTimeout(() => this.updateNavigationState(), 120)
        })
      })
    },
    showPreviousExhibitions(event) {
      event?.stopImmediatePropagation()
      this.updateNavigationState()
      if (this.exhibitionSwiper && this.canMovePrev) this.exhibitionSwiper.slidePrev()
    },
    showNextExhibitions(event) {
      event?.stopImmediatePropagation()
      this.updateNavigationState()
      if (this.exhibitionSwiper && this.canMoveNext) this.exhibitionSwiper.slideNext()
    },
    handleExhibitionClick(exhibition, event) {
      const targetUrl = this.getTargetUrl(exhibition)
      if (!targetUrl) return
      event?.preventDefault()
      window.location.assign(targetUrl)
    },
    updateNavigationState() {
      const swiper = this.exhibitionSwiper
      if (!swiper || !swiper.el || !swiper.slides || !swiper.slides.length) return
      const firstSlide = swiper.slides[0]
      const lastSlide = swiper.slides[swiper.slides.length - 1]
      if (!firstSlide || !lastSlide) return
      const containerRect = swiper.el.getBoundingClientRect()
      const firstSlideRect = firstSlide.getBoundingClientRect()
      const lastSlideRect = lastSlide.getBoundingClientRect()
      const navigationState = getExhibitionNavigationState(containerRect, firstSlideRect, lastSlideRect, 2)
      this.canMovePrev = navigationState.canMovePrev
      this.canMoveNext = navigationState.canMoveNext
    },
    updateSwiperState() {
      if (!this.exhibitionSwiper) return
      this.activeIndex = this.exhibitionSwiper.activeIndex
      this.updateNavigationState()
    }
  }
}
</script>

<style scoped>
.exhibition-schedule,
.exhibition-schedule * {
  box-sizing: border-box;
}

.exhibition-schedule {
  width: 100%;
  height: 766px;
  overflow: hidden;
  background: #fff;
  color: #000;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
}

.exhibition-schedule__content {
  position: relative;
  width: 100%;
  max-width: 1440px;
  height: 766px;
  margin: 0 auto;
  overflow: hidden;
}

.exhibition-schedule__heading {
  position: absolute;
  top: 112px;
  left: 50%;
  width: 100%;
  text-align: center;
  transform: translateX(-50%);
}

.exhibition-schedule__heading h2,
.exhibition-schedule__heading p {
  margin: 0;
}

.exhibition-schedule__heading h2 {
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 28px;
  line-height: 1.3;
}

.exhibition-schedule__heading p {
  margin-top: 10px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

.exhibition-swiper {
  position: absolute;
  top: 206px;
  left: 50%;
  width: 100%;
  height: 330px;
  overflow: visible;
  transform: translateX(-50%);
}

.exhibition-swiper__wrapper {
  display: flex;
  height: 330px;
}

.exhibition-card {
  position: relative;
  display: block;
  flex: 0 0 330px;
  width: 330px;
  max-width: 330px;
  height: 330px;
  overflow: hidden;
  background: #252525;
  color: #fff;
  text-align: center;
  cursor: default;
  transition: background-color 0.25s ease, transform 0.25s ease;
}

.exhibition-card.is-clickable:hover,
.exhibition-card.is-clickable:focus-visible {
  background: #ef0a6a;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.22);
  transform: translateY(-6px);
}

.exhibition-card.is-clickable { cursor: pointer; }

.exhibition-card.is-ended { opacity: 0.5; }

.exhibition-card.is-ended.is-clickable:hover,
.exhibition-card.is-ended.is-clickable:focus-visible { opacity: 1; }

.exhibition-card.is-clickable:hover .exhibition-card__image,
.exhibition-card.is-clickable:focus-visible .exhibition-card__image {
  opacity: 0.42;
  transform: scale(1.035);
}

.exhibition-card.is-clickable:hover .exhibition-card__overlay,
.exhibition-card.is-clickable:focus-visible .exhibition-card__overlay {
  background: rgba(239, 10, 106, 0.55);
}

.exhibition-card:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: -3px;
}

.exhibition-card__image,
.exhibition-card__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.exhibition-card__image {
  display: block;
  max-width: none;
  object-fit: cover;
  opacity: 0.5;
  transition: opacity 0.25s ease, transform 0.35s ease;
}

.exhibition-media {
  opacity: 0;
  transition: opacity 0.24s ease, transform 0.35s ease;
}

.exhibition-media.is-media-ready { opacity: 0.5; }

.exhibition-card__overlay {
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0.38));
  transition: background-color 0.25s ease;
}

.exhibition-card__copy {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}

.exhibition-card__copy h3 {
  margin: 28px 12px 0;
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 20px;
  line-height: 1.35;
  white-space: pre-line;
}

.exhibition-card__copy p {
  margin: 2px 12px 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.exhibition-card__copy strong {
  position: absolute;
  right: 12px;
  bottom: 30px;
  left: 12px;
  color: #fff;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 12px;
}

.exhibition-card.has-status-strip .exhibition-card__copy strong { bottom: 54px; }

.exhibition-card__status {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background: #4b4b4b;
  font-family: 'MiSans-Bold', sans-serif;
  font-size: 12px;
}

.exhibition-card__status span { position: relative; z-index: 1; }

.exhibition-card__status-texture {
  position: absolute;
  top: 0;
  left: -25px;
  width: 380px;
  height: 40px;
  pointer-events: none;
}

.exhibition-card__status.is-ongoing { background: rgba(239, 10, 106, 0.5); }
.exhibition-card__status.is-upcoming { display: none; }

.exhibition-schedule__controls {
  position: absolute;
  right: 72px;
  bottom: 107px;
  display: flex;
  gap: 20px;
}

.exhibition-schedule__controls .legacy-carousel-button {
  width: 40px;
  height: 40px;
}

@media (max-width: 1023px) {
  .exhibition-schedule__controls {
    right: 24px;
  }
}

@media (max-width: 700px) {
  .exhibition-schedule,
  .exhibition-schedule__content {
    height: 647px;
  }

  .exhibition-schedule__heading {
    top: 58px;
    padding: 0 16px;
  }

  .exhibition-schedule__heading h2 {
    font-size: 26px;
  }

  .exhibition-schedule__heading p {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.4;
  }

  .exhibition-swiper {
    top: 153px;
    height: min(374px, calc(100vw - 32px));
  }

  .exhibition-swiper__wrapper,
  .exhibition-card {
    height: min(374px, calc(100vw - 32px));
  }

  .exhibition-card {
    flex-basis: min(374px, calc(100vw - 32px));
    width: min(374px, calc(100vw - 32px));
    max-width: none;
  }

  .exhibition-schedule__controls {
    right: auto;
    bottom: 36px;
    left: 16px;
  }

}

@media (prefers-reduced-motion: reduce) {
  .exhibition-card,
  .exhibition-media {
    transition: none;
  }
}
</style>
